import {
    MILITARY_VARIANT,
    STC_VARIANT,
    collection,
    isMilitaryEnabled,
    parentTypeFor,
} from "../_variants/index.js";
import {
    addDrawing,
    cancelDrawing,
    changeLinkedRevision,
    checkDrawing,
    editDrawing,
    linkDrawing,
    makePrototype,
    reviseDrawing,
    reviseDrawingToA,
    revisionRollback,
    unlinkDrawing
} from "./actions";

import {
    get
} from "lodash";
import { isPrototypeRevision } from "../../../utils";
import store from "../../../store";

const appConfig = JSON.parse(import.meta.env.VITE_APP_CONFIG);

function warning(key, pageItem) {
    let item = store.state[key];
    if (!item) return false;
    if (item.Cancelled) return false;

    if (!item?.Revisions) return "No revisions found"
    let warningMessage = "";
    console.log(item);
    if (!item.Revisions[item?.Links[pageItem.ID]]?.Quality?.Approver?.Completed && !item.Cancelled) {
        warningMessage = warningMessage += `${item.Number} not signed off by approver.<br>`;
    }
    
    if(item.RequiresStressCheck && !item.Revisions[item?.Links[pageItem.ID]]?.Quality?.StressChecker?.Completed && !item.Cancelled) {
        warningMessage = warningMessage += `${item.Number} not signed off by stress checker.<br>`;
    }

    if (isPrototypeRevision(item.Revision)) {
        warningMessage = warningMessage += `${item.Number} at a prototype revision.<br>`;
    }

    return warningMessage === "" ? false : warningMessage;
}

const cellClassRules = {
    "text-orange font-bold": params => {
        return warning(params.data);
    }
}

function linkedRevisionCompleted(params, role) {
    const linkedRevision = get(params.data, params.column.colDef.field);
    return get(params.data, `Revisions.${linkedRevision}.Quality.${role}.Completed`, false);
}

const compilerCompleted = params => linkedRevisionCompleted(params, "Compiler");
const checkerCompleted = params => linkedRevisionCompleted(params, "Checker");
const approverCompleted = params => linkedRevisionCompleted(params, "Approver");
const stressCheckerCompleted = params => {
    if(params.data.RequiresStressCheck === false) return "N/A";
    return linkedRevisionCompleted(params, "StressChecker");
};

const headers = [{
    "headerName": "Number",
    "field": "Number",
    "sort": "asc",
    cellClassRules
}, {
    "headerName": "Title",
    "field": "Title",
    cellClassRules
}, {
    "headerName": "Linked Revision",
    cellClassRules,
    fieldGetter({ pageItem }) {
        return `Links.${pageItem.ID}`
    },
    valueFormatter(params) {
        let linkedRev = get(params.data, params.column.colDef.field);
        let revision = get(params.data, `Revisions.${linkedRev}`);
        return revision?.Revision || params.data.Revision;
    }
}, {
    "headerName": "Compiler Sign Off",
    "type": "bool",
    fieldGetter({ pageItem }) {
        return `Links.${pageItem.ID}`
    },
    valueGetter: compilerCompleted,
    valueFormatter: compilerCompleted
}, {
    "headerName": "Checker Sign Off",
    "type": "bool",
    fieldGetter({ pageItem }) {
        return `Links.${pageItem.ID}`
    },
    valueGetter: checkerCompleted,
    valueFormatter: checkerCompleted
}, {
    "headerName": "Approver Sign Off",
    "type": "bool",
    fieldGetter({ pageItem }) {
        return `Links.${pageItem.ID}`
    },
    valueGetter: approverCompleted,
    valueFormatter: approverCompleted
    }, {
        "headerName": "Stress Checked",
        "type": "bool",
        fieldGetter({ pageItem }) {
            return `Links.${pageItem.ID}`
        },
        valueGetter: stressCheckerCompleted,
        valueFormatter: stressCheckerCompleted
    }
];

/**
 * Build the attachable-table descriptor for the Drawing register that matches
 * the supplied variant. Row actions rely on the parent's `meta.ParentType`
 * (set on the parent route's `formMetadata`) to derive the drawing's variant
 * at action time.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDrawingAttachable = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military Drawing" : "Drawing";
    const labelPlural = isMilitary ? "Military Drawings" : "Drawings";
    const drawingParentType = parentTypeFor(variant, "Drawings");
    const drawingCollection = collection(variant, "Drawings");

    const actions = [{
        "label": `Add ${labelSingular}`,
        "action": addDrawing,
        show({ meta }) {
            return [["DesignChanges", "FlightConditions", "Repairs", "MilitaryDesignChanges", "MilitaryFlightConditions", "MilitaryRepairs"].includes(meta?.ParentType), `${labelPlural} cannot be added here, they must be linked.`]
        }
    }, {
        "label": `Link ${labelSingular}`,
        "action": linkDrawing
    }];

    const rowActions = [{
        "label": `View ${labelSingular}`,
        action({ router, rowItem }) {
            router.push(`/Database/${drawingParentType}/${rowItem.ID}`)
        }
    }, {
        "break": true,
        "label": `Edit ${labelSingular}`,
        "action": editDrawing
    }, {
        "label": `Revise ${labelSingular}`,
        "break": true,
        "action": reviseDrawing,
        show({ item }) {
            return item.Revisions[item.RevisionID].Quality.Approver.Completed ?
                [true, ""] :
                [false, "Cannot revise this drawing until it has been approved."]
        }
    }, {
        "label": "Revise from Prototype",
        "action": reviseDrawingToA,
        show({ item }) {
            return isPrototypeRevision(item.Revision) ? [true, ""] : [false, "Revision not at prototype"];
        }
    }, {
        "label": "Revise to Prototype",
        "action": makePrototype,
        show({ item }) {
            return isPrototypeRevision(item.Revision) ? [false, "Revision already a prototype"] : [true, ""];
        }
    }, {
        "label": "Rollback Revision",
        "action": revisionRollback,
        show({ item }) {
            return [Object.keys(item.Revisions).length > 1, "No previous revisions found"];
        }
    }, {
        "label": "Change Linked Revision",
        "action": changeLinkedRevision
    }, {
        "label": "Compiler Check",
        "action": checkDrawing("Compiler"),
        "break": true,
        show({ item }) {
            return item.Revisions[item.RevisionID].Quality.Compiler.Completed === true ? [false, "Already Complier Checked"] : [true, ""];
        }
    },
    {
        "label": "Checker Check",
        "action": checkDrawing("Checker"),
        "security": "Database.DrawingChecker",
        show({ item }) {
            return item.Revisions[item.RevisionID].Quality.Checker.Completed === true ?
                [false, "Already Complier Checked"] :
                !item.Revisions[item.RevisionID].Quality.Compiler.Completed ?
                    [false, "Drawing not checked by the complier"] :
                    [true, ""]
        }
    },
    {
        "label": "Approver Check",
        "security": "Database.DrawingApprover",
        "action": checkDrawing("Approver"),
        show({ item }) {
            return item.Revisions[item.RevisionID].Quality.Approver.Completed === true ?
                [false, "Already Approver Checked"] :
                !item.Revisions[item.RevisionID].Quality.Checker.Completed ?
                    [false, "Drawing not checked by the checker"] :
                    [true, ""];
        }
    },
    {
        "label": "Stress Check",
        "action": checkDrawing("StressChecker"),
        show({ item }) {
            return item.RequiresStressCheck === false ?
                [false, "Drawing does not require stress checking."] :
                item.Revisions[item.RevisionID].Quality.StressChecker.Completed === true ?
                    [false, "Already stress checked"] :
                    [true, ""];
        }
    }, {
        "label": "View Archived File",
        "break": true,
        async action({ toast, rowItem, db }) {
            try {

                let rev = rowItem.Revision;
                let url = await db.download(`${drawingCollection}/${rowItem.ID}/${rowItem.Number}_${rev}.pdf`);
                window.open(url, "__blank")
            } catch (err) {
                toast.error("Archive file found");
            }
        }
    }, {
        "label": `Unlink ${labelSingular} from Parent`,
        "break": true,
        "action": unlinkDrawing,
        show({ rowItem, pageItem }) {
            return [rowItem.ParentID !== pageItem.ID, "Cannot unlink from the main parent"]
        }
    }, {
        "label": `Cancel ${labelSingular}`,
        "break": true,
        "action": cancelDrawing,
        "class": "text-red font-bold",
    }];

    return {
        "label": labelPlural,
        "key": drawingParentType,
        "location": `${drawingCollection}/`,
        actions,
        rowActions,
        headers,
        warn({ items, pageItem }) {
            let warningMessage = items.map(i => warning(i, pageItem)).filter(i => i !== false).join("<br>");
            return [warningMessage !== "", warningMessage];
        }
    };
};

export const drawingAttachable = makeDrawingAttachable(STC_VARIANT);
export const militaryDrawingAttachable = isMilitaryEnabled() ? makeDrawingAttachable(MILITARY_VARIANT) : null;

export default drawingAttachable;
